import {Box, Checkbox, CircularProgress, InputAdornment, TextField, Typography} from "@mui/material";
import {Search as SearchIcon} from "@mui/icons-material";
import {useEffect, useState} from "react";
import {getImpersonatableCompanies} from "../../services/system/CompanyGroupAdminService";
import {colors, borders, avatarPalette} from "../ui/colors";

const inputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: borders.radius.md,
        backgroundColor: colors.white,
        "&:hover .MuiOutlinedInput-notchedOutline": {borderColor: colors.primary},
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: colors.primary, borderWidth: "2px"}
    },
    "& .MuiOutlinedInput-notchedOutline": {borderColor: colors.border}
};

const labelSx = {fontSize: "13px", fontWeight: 500, color: colors.textSecondary, marginBottom: "6px"};

const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.trim().slice(0, 2).toUpperCase();
};

const getColor = (company) => {
    const key = String(company?.id ?? company?.name ?? "");
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash);
    return avatarPalette[Math.abs(hash) % avatarPalette.length];
};

// Controlled "companies authorized for impersonation" multi-select. Shared by the create drawer
// (AddCompanyGroupAdminDialog) and the edit dialog (EditAuthorizedCompaniesDialog) so both offer the
// exact same picker. Selection is fully controlled via `value` (array of company ids) + `onChange`;
// the full company list is (re)fetched whenever `active` becomes true so a dialog only loads when open.
const ImpersonationCompaniesPicker = ({active = true, value = [], onChange}) => {
    const [companies, setCompanies] = useState([]);
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (!active) return;
        setSearch("");
        getImpersonatableCompanies()
            .then((res) => setCompanies(res.data || []))
            .catch(() => setCompanies([]));
    }, [active]);

    const filtered = companies.filter((c) =>
        c?.name?.toLowerCase().includes(search.trim().toLowerCase())
    );
    const allSelected = filtered.length > 0 && filtered.every((c) => value.includes(c.id));

    const toggleAll = () => {
        onChange?.(allSelected ? value.filter((id) => !filtered.some((c) => c.id === id))
            : [...new Set([...value, ...filtered.map((c) => c.id)])]);
    };
    const toggle = (id) => {
        onChange?.(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
    };

    return (
        <Box>
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "baseline"}}>
                <Typography sx={labelSx}>חברות מורשות להתחזות</Typography>
                <Typography sx={{fontSize: "12px", color: colors.textMuted}}>{value.length} נבחרו</Typography>
            </Box>
            <Typography sx={{fontSize: "12px", color: colors.textMuted, marginBottom: "8px", lineHeight: 1.5}}>
                בחר את החברות שאותן מנהל זה יוכל לנהל בהתחזות, עם הרשאות מנהל החברה.
            </Typography>

            <TextField sx={inputSx} fullWidth size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="חפש חברה..."
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{color: colors.textMuted, fontSize: "18px"}}/>
                        </InputAdornment>
                    )
                }}
            />

            {/* Picker header */}
            <Box sx={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                marginTop: "10px", padding: "6px 10px",
                backgroundColor: colors.primaryLight,
                borderRadius: `${borders.radius.sm} ${borders.radius.sm} 0 0`
            }}>
                <Typography sx={{fontSize: "12px", fontWeight: 600, color: colors.primary}}>שם חברה</Typography>
                <Box onClick={toggleAll} sx={{display: "flex", alignItems: "center", gap: 0.25, cursor: "pointer"}}>
                    <Typography sx={{fontSize: "12px", fontWeight: 600, color: colors.primary}}>בחר הכל</Typography>
                    <Checkbox checked={allSelected} size="small"
                        sx={{padding: 0, color: colors.primary, "&.Mui-checked": {color: colors.primary}}}/>
                </Box>
            </Box>

            {/* Picker list */}
            <Box sx={{
                maxHeight: 220, overflowY: "auto",
                border: borders.default, borderTop: "none",
                borderRadius: `0 0 ${borders.radius.sm} ${borders.radius.sm}`,
                backgroundColor: colors.white
            }}>
                {companies.length === 0 ? (
                    <Box sx={{display: "flex", justifyContent: "center", padding: "20px"}}>
                        <CircularProgress size={20} sx={{color: colors.primary}}/>
                    </Box>
                ) : filtered.length === 0 ? (
                    <Typography sx={{fontSize: "12px", color: colors.textMuted, textAlign: "center", padding: "16px"}}>
                        לא נמצאו חברות
                    </Typography>
                ) : filtered.map((c) => (
                    <Box key={c.id} onClick={() => toggle(c.id)} sx={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "6px 10px", cursor: "pointer",
                        borderBottom: borders.default,
                        "&:last-of-type": {borderBottom: "none"},
                        "&:hover": {backgroundColor: colors.bgHover}
                    }}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1, minWidth: 0}}>
                            <Box sx={{
                                width: 28, height: 28, borderRadius: borders.radius.sm,
                                backgroundColor: getColor(c), color: colors.white,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "10px", fontWeight: 700, flexShrink: 0
                            }}>
                                {getInitials(c.name)}
                            </Box>
                            <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: colors.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                {c.name}
                            </Typography>
                        </Box>
                        <Checkbox checked={value.includes(c.id)} size="small"
                            sx={{padding: 0, color: colors.border, "&.Mui-checked": {color: colors.primary}}}/>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default ImpersonationCompaniesPicker;
